import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Autocomplete,
  TextField,
} from '@mui/material';

const AssignmentDialog = ({
  open,
  mode,
  currentAssignmentClient,
  selectedEmployee,
  employees,
  unassignedClients,
  onClose,
  onSubmit,
  onClientChange,
  onEmployeeChange,
}) => {
  const getDialogTitle = () => {
    return mode === 'edit' ? 'Edit Assignment' : 'New Assignment';
  };

  const isDisabled = !selectedEmployee || (mode === 'add' && !currentAssignmentClient);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{getDialogTitle()}</DialogTitle>
      <DialogContent>
        {mode === 'edit' ? (
          currentAssignmentClient && (
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Client: {currentAssignmentClient.practice_name}
            </Typography>
          )
        ) : (
          <Box sx={{ mb: 2, pt: 1 }}>
            <Autocomplete
              options={unassignedClients}
              getOptionLabel={(option) => `${option.practice_name} (${option.email})`}
              value={currentAssignmentClient}
              onChange={(event, newValue) => {
                onClientChange(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Client"
                  placeholder="Search clients..."
                  variant="outlined"
                  fullWidth
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2">{option.practice_name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {option.email}
                    </Typography>
                  </Box>
                </Box>
              )}
              noOptionsText="No unassigned clients available"
              fullWidth
            />
          </Box>
        )}
        <Box sx={{ mt: 2 }}>
          <Autocomplete
            options={employees}
            getOptionLabel={(option) => `${option.full_name} - ${option.email}`}
            value={selectedEmployee}
            onChange={(event, newValue) => {
              onEmployeeChange(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Employee"
                placeholder="Search employees..."
                variant="outlined"
                fullWidth
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2">{option.full_name}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {option.email}
                  </Typography>
                </Box>
              </Box>
            )}
            noOptionsText="No employees available"
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSubmit} variant="contained" disabled={isDisabled}>
          {mode === 'edit' ? 'Update Assignment' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AssignmentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  currentAssignmentClient: PropTypes.object,
  selectedEmployee: PropTypes.object,
  employees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      full_name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    })
  ),
  unassignedClients: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      practice_name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    })
  ),
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClientChange: PropTypes.func.isRequired,
  onEmployeeChange: PropTypes.func.isRequired,
};

AssignmentDialog.defaultProps = {
  currentAssignmentClient: null,
  selectedEmployee: null,
  employees: [],
  unassignedClients: [],
};

export default AssignmentDialog;
